/**
 * Bank Accounts API Endpoints
 *
 * Handles all bank account related API calls including:
 * - Bank catalog management
 * - Bank account CRUD operations
 * - Client-bank account associations
 * - Bank reconciliation
 */

import { GET, POST, PUT, DELETE, PATCH } from "../config";

// ============================================================
// BANKS - Bank Catalog
// ============================================================

/**
 * Get all banks
 * @param {Object} params - Query parameters (country, is_active, show_inactive)
 * @returns {Promise} List of banks
 */
export const getBanks = async (params = {}) => {
  const queryParams = new URLSearchParams(params).toString();
  const url = `finance/banks/${queryParams ? `?${queryParams}` : ""}`;
  return await GET(url);
};

/**
 * Get a single bank by ID
 * @param {string} id - Bank ID
 * @returns {Promise} Bank details
 */
export const getBankById = async (id) => {
  return await GET(`finance/banks/${id}/`);
};

/**
 * Create a new bank
 * @param {Object} data - Bank data
 * @returns {Promise} Created bank
 */
export const createBank = async (data) => {
  return await POST("finance/banks/", data);
};

/**
 * Update an existing bank
 * @param {string} id - Bank ID
 * @param {Object} data - Updated bank data
 * @returns {Promise} Updated bank
 */
export const updateBank = async (id, data) => {
  return await PUT(`finance/banks/${id}/`, data);
};

/**
 * Delete a bank
 * @param {string} id - Bank ID
 * @returns {Promise} Deletion confirmation
 */
export const deleteBank = async (id) => {
  return await DELETE(`finance/banks/${id}/`);
};

/**
 * Get popular banks based on account count
 * @returns {Promise} List of popular banks
 */
export const getPopularBanks = async () => {
  return await GET("finance/banks/popular/");
};

// ============================================================
// BANK ACCOUNTS - Company Bank Accounts
// ============================================================

/**
 * Get all bank accounts
 * @param {Object} params - Query parameters (branch, bank, account_type, is_default, show_inactive)
 * @returns {Promise} List of bank accounts
 */
export const getBankAccounts = async (params = {}) => {
  const queryParams = new URLSearchParams(params).toString();
  const url = `finance/bank-accounts/${queryParams ? `?${queryParams}` : ""}`;
  return await GET(url);
};

/**
 * Get a single bank account by ID
 * @param {string} id - Bank account ID
 * @returns {Promise} Bank account details
 */
export const getBankAccountById = async (id) => {
  return await GET(`finance/bank-accounts/${id}/`);
};

/**
 * Create a new bank account
 * @param {Object} data - Bank account data
 * @returns {Promise} Created bank account
 */
export const createBankAccount = async (data) => {
  return await POST("finance/bank-accounts/", data);
};

/**
 * Update an existing bank account
 * @param {string} id - Bank account ID
 * @param {Object} data - Updated bank account data
 * @returns {Promise} Updated bank account
 */
export const updateBankAccount = async (id, data) => {
  return await PUT(`finance/bank-accounts/${id}/`, data);
};

/**
 * Partially update a bank account
 * @param {string} id - Bank account ID
 * @param {Object} data - Partial bank account data
 * @returns {Promise} Updated bank account
 */
export const patchBankAccount = async (id, data) => {
  return await PATCH(`finance/bank-accounts/${id}/`, data);
};

/**
 * Delete a bank account
 * @param {string} id - Bank account ID
 * @returns {Promise} Deletion confirmation
 */
export const deleteBankAccount = async (id) => {
  return await DELETE(`finance/bank-accounts/${id}/`);
};

/**
 * Get balance summary for a bank account
 * @param {string} id - Bank account ID
 * @returns {Promise} Balance summary
 */
export const getBankAccountBalanceSummary = async (id) => {
  return await GET(`finance/bank-accounts/${id}/balance_summary/`);
};

/**
 * Get transactions for a bank account
 * @param {string} id - Bank account ID
 * @param {Object} params - Optional query parameters (start_date, end_date)
 * @returns {Promise} List of transactions
 */
export const getBankAccountTransactions = async (id, params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.start_date) queryParams.append('start_date', params.start_date);
  if (params.end_date) queryParams.append('end_date', params.end_date);
  
  const url = `finance/bank-accounts/${id}/transactions/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  return await GET(url);
};

/**
 * Get the default bank account for the current branch
 * @returns {Promise} Default bank account
 */
export const getDefaultBankAccount = async () => {
  return await GET("finance/bank-accounts/default_account/");
};

/**
 * Set a bank account as default for the branch
 * @param {string} id - Bank account ID
 * @returns {Promise} Updated bank account
 */
export const setDefaultBankAccount = async (id) => {
  return await POST(`finance/bank-accounts/${id}/set_as_default/`);
};

/**
 * Get summary of all accounts by branch
 * @returns {Promise} Accounts summary
 */
export const getBankAccountsSummaryByBranch = async () => {
  return await GET("finance/bank-accounts/summary_by_branch/");
};

// ============================================================
// CLIENT BANK ACCOUNTS - Client/Supplier Associations
// ============================================================

/**
 * Get all client-bank account associations
 * @param {Object} params - Query parameters
 * @returns {Promise} List of associations
 */
export const getClientBankAccounts = async (params = {}) => {
  const queryParams = new URLSearchParams(params).toString();
  const url = `finance/client-bank-accounts/${
    queryParams ? `?${queryParams}` : ""
  }`;
  return await GET(url);
};

/**
 * Get a single client-bank account association
 * @param {string} id - Association ID
 * @returns {Promise} Association details
 */
export const getClientBankAccountById = async (id) => {
  return await GET(`finance/client-bank-accounts/${id}/`);
};

/**
 * Create a new client-bank account association
 * @param {Object} data - Association data
 * @returns {Promise} Created association
 */
export const createClientBankAccount = async (data) => {
  return await POST("finance/client-bank-accounts/", data);
};

/**
 * Update a client-bank account association
 * @param {string} id - Association ID
 * @param {Object} data - Updated association data
 * @returns {Promise} Updated association
 */
export const updateClientBankAccount = async (id, data) => {
  return await PUT(`finance/client-bank-accounts/${id}/`, data);
};

/**
 * Delete a client-bank account association
 * @param {string} id - Association ID
 * @returns {Promise} Deletion confirmation
 */
export const deleteClientBankAccount = async (id) => {
  return await DELETE(`finance/client-bank-accounts/${id}/`);
};

/**
 * Get all bank account associations for a specific client
 * @param {string} clientId - Client ID
 * @returns {Promise} List of associations
 */
export const getClientBankAccountsByClient = async (clientId) => {
  return await GET(
    `finance/client-bank-accounts/by_client/?client_id=${clientId}`
  );
};

/**
 * Get all client associations for a specific bank account
 * @param {string} accountId - Bank account ID
 * @returns {Promise} List of associations
 */
export const getClientBankAccountsByAccount = async (accountId) => {
  return await GET(
    `finance/client-bank-accounts/by_account/?account_id=${accountId}`
  );
};

/**
 * Set an association as default for a client
 * @param {string} associationId - Association ID
 * @returns {Promise} Updated association
 */
export const setDefaultClientBankAccount = async (associationId) => {
  return await POST("finance/client-bank-accounts/set_default/", {
    association_id: associationId,
  });
};

// ============================================================
// BANK RECONCILIATION
// ============================================================

/**
 * Get all bank reconciliations
 * @param {Object} params - Query parameters
 * @returns {Promise} List of reconciliations
 */
export const getBankReconciliations = async (params = {}) => {
  const queryParams = new URLSearchParams(params).toString();
  const url = `finance/bank-reconciliations/${
    queryParams ? `?${queryParams}` : ""
  }`;
  return await GET(url);
};

/**
 * Get a single bank reconciliation
 * @param {string} id - Reconciliation ID
 * @returns {Promise} Reconciliation details
 */
export const getBankReconciliationById = async (id) => {
  return await GET(`finance/bank-reconciliations/${id}/`);
};

/**
 * Create a new bank reconciliation
 * @param {Object} data - Reconciliation data
 * @returns {Promise} Created reconciliation
 */
export const createBankReconciliation = async (data) => {
  return await POST(
    "finance/bank-reconciliations/create_reconciliation/",
    data
  );
};

/**
 * Update a bank reconciliation
 * @param {string} id - Reconciliation ID
 * @param {Object} data - Updated reconciliation data
 * @returns {Promise} Updated reconciliation
 */
export const updateBankReconciliation = async (id, data) => {
  return await PATCH(`finance/bank-reconciliations/${id}/`, data);
};

/**
 * Delete a bank reconciliation
 * @param {string} id - Reconciliation ID
 * @returns {Promise} Deletion confirmation
 */
export const deleteBankReconciliation = async (id) => {
  return await DELETE(`finance/bank-reconciliations/${id}/`);
};

/**
 * Mark a reconciliation as balanced
 * @param {string} id - Reconciliation ID
 * @returns {Promise} Updated reconciliation
 */
export const markReconciliationAsBalanced = async (id) => {
  return await POST(`finance/bank-reconciliations/${id}/mark_as_balanced/`);
};

/**
 * Validate and mark reconciliation as completed (even if not balanced)
 * @param {string} id - Reconciliation ID
 * @returns {Promise} Updated reconciliation
 */
export const validateReconciliation = async (id) => {
  return await POST(`finance/bank-reconciliations/${id}/validate_reconciliation/`);
};

/**
 * Mark a reconciliation as pending
 * @param {string} id - Reconciliation ID
 * @returns {Promise} Updated reconciliation
 */
export const markReconciliationAsPending = async (id) => {
  return await POST(`finance/bank-reconciliations/${id}/mark_as_pending/`);
};

/**
 * Get all pending reconciliations
 * @returns {Promise} List of pending reconciliations
 */
export const getPendingReconciliations = async () => {
  return await GET("finance/bank-reconciliations/pending_reconciliations/");
};

/**
 * Get reconciliation summary
 * @returns {Promise} Reconciliation summary statistics
 */
export const getReconciliationSummary = async () => {
  return await GET("finance/bank-reconciliations/summary/");
};

// ============================================================
// UTILITY FUNCTIONS
// ============================================================

/**
 * Get Chilean bank choices (commonly used banks in Chile)
 * @returns {Array} List of Chilean bank choices
 */
export const getChileanBankChoices = () => {
  return [
    { value: "BANCO_CHILE", label: "Banco de Chile" },
    { value: "BANCO_ESTADO", label: "BancoEstado" },
    { value: "SANTANDER", label: "Banco Santander" },
    { value: "BCI", label: "Banco de Crédito e Inversiones (BCI)" },
    { value: "SCOTIABANK", label: "Scotiabank" },
    { value: "ITAU", label: "Itaú" },
    { value: "SECURITY", label: "Banco Security" },
    { value: "FALABELLA", label: "Banco Falabella" },
    { value: "RIPLEY", label: "Banco Ripley" },
    { value: "CONSORCIO", label: "Banco Consorcio" },
    { value: "BICE", label: "Banco BICE" },
    { value: "HSBC", label: "HSBC Bank" },
    { value: "BBVA", label: "BBVA" },
    { value: "COOPEUCH", label: "Coopeuch" },
    { value: "OTHER", label: "Otro" },
  ];
};

/**
 * Get account type choices
 * @returns {Array} List of account type choices
 */
export const getAccountTypeChoices = () => {
  return [
    { value: "CHECKING", label: "Cuenta Corriente" },
    { value: "SAVINGS", label: "Cuenta de Ahorro" },
    { value: "BUSINESS", label: "Cuenta Empresarial" },
    { value: "INVESTMENT", label: "Cuenta de Inversión" },
    { value: "OTHER", label: "Otro" },
  ];
};

/**
 * Get purpose choices for client-bank account associations
 * @returns {Array} List of purpose choices
 */
export const getPurposeChoices = () => {
  return [
    { value: "INCOME", label: "Ingresos" },
    { value: "EXPENSE", label: "Egresos" },
    { value: "BOTH", label: "Ambos" },
  ];
};

/**
 * Get reconciliation status choices
 * @returns {Array} List of reconciliation status choices
 */
export const getReconciliationStatusChoices = () => {
  return [
    { value: "PENDING", label: "Pendiente" },
    { value: "BALANCED", label: "Balanceado" },
    { value: "DISCREPANCY", label: "Discrepancia" },
  ];
};
